import { LANGUAGES } from '@app/core/constants/language.constants';

export const COURSE_INTENTS = {
  KNOWLEDGE_TRANSFER: 'knowledge_transfer',
  COMPLIANCE_AWARENESS: 'awareness_compliance',
  SKILLS_BUILDING: 'skill_building',
  ONBOARDING: 'onboarding',
  CERTIFICATION_PREP: 'certification_prep',
  AI_DECIDE: 'unknown',
} as const;

export type CourseIntent = (typeof COURSE_INTENTS)[keyof typeof COURSE_INTENTS];

export const TARGET_AUDIENCES = {
  NEW_TO_TOPIC: 'new_to_topic',
  SOME_EXPERIENCE: 'some_experience',
  EXPERTS: 'experts',
} as const;

export type TargetAudience =
  (typeof TARGET_AUDIENCES)[keyof typeof TARGET_AUDIENCES];

export interface OptionConfig<T extends string = string> {
  readonly id: T;
  readonly titleKey: string;
  readonly descriptionKey: string;
  readonly icon: string;
}

export const COURSE_INTENT_OPTIONS: readonly OptionConfig<CourseIntent>[] = [
  {
    id: COURSE_INTENTS.KNOWLEDGE_TRANSFER,
    titleKey: 'guidedCourseCreation.intent.knowledgeTransfer.title',
    descriptionKey: 'guidedCourseCreation.intent.knowledgeTransfer.description',
    icon: 'auto_stories',
  },
  {
    id: COURSE_INTENTS.COMPLIANCE_AWARENESS,
    titleKey: 'guidedCourseCreation.intent.complianceAwareness.title',
    descriptionKey:
      'guidedCourseCreation.intent.complianceAwareness.description',
    icon: 'shield',
  },
  {
    id: COURSE_INTENTS.SKILLS_BUILDING,
    titleKey: 'guidedCourseCreation.intent.skillsBuilding.title',
    descriptionKey: 'guidedCourseCreation.intent.skillsBuilding.description',
    icon: 'build',
  },
  {
    id: COURSE_INTENTS.ONBOARDING,
    titleKey: 'guidedCourseCreation.intent.onboarding.title',
    descriptionKey: 'guidedCourseCreation.intent.onboarding.description',
    icon: 'person_add',
  },
  {
    id: COURSE_INTENTS.CERTIFICATION_PREP,
    titleKey: 'guidedCourseCreation.intent.certificationPrep.title',
    descriptionKey: 'guidedCourseCreation.intent.certificationPrep.description',
    icon: 'workspace_premium',
  },
  {
    id: COURSE_INTENTS.AI_DECIDE,
    titleKey: 'guidedCourseCreation.intent.aiDecide.title',
    descriptionKey: 'guidedCourseCreation.intent.aiDecide.description',
    icon: 'smart_toy',
  },
];

export const TARGET_AUDIENCE_OPTIONS: readonly OptionConfig<TargetAudience>[] =
  [
    {
      id: TARGET_AUDIENCES.NEW_TO_TOPIC,
      titleKey: 'guidedCourseCreation.audience.newToTopic.title',
      descriptionKey: 'guidedCourseCreation.audience.newToTopic.description',
      icon: 'face',
    },
    {
      id: TARGET_AUDIENCES.SOME_EXPERIENCE,
      titleKey: 'guidedCourseCreation.audience.someExperience.title',
      descriptionKey:
        'guidedCourseCreation.audience.someExperience.description',
      icon: 'hail',
    },
    {
      id: TARGET_AUDIENCES.EXPERTS,
      titleKey: 'guidedCourseCreation.audience.experts.title',
      descriptionKey: 'guidedCourseCreation.audience.experts.description',
      icon: 'work',
    },
  ];

interface ModuleTypeConfig {
  readonly displayKey: string;
  readonly iconType: string;
  readonly h5pTitle: string;
}

type SuggestedModuleType =
  | 'interactive_video'
  | 'content'
  | 'checklist'
  | 'flashcards'
  | 'image_hotspots'
  | 'image_slider'
  | 'find_the_hotspots'
  | 'drag_the_words'
  | 'task'
  | 'quiz'
  | 'audio'
  | 'file_upload'
  | 'feedback_module'
  | 'link'
  | 'memory'
  | 'sort_the_paragraphs';

export const MODULE_TYPE_MAP: Record<SuggestedModuleType, ModuleTypeConfig> = {
  interactive_video: {
    displayKey: 'h5p.libraries.H5P.InteractiveVideo',
    iconType: 'Interactive video',
    h5pTitle: 'Interactive Video',
  },
  content: {
    displayKey: 'h5p.libraries.H5P.ContentPage',
    iconType: 'Content page',
    h5pTitle: 'Content',
  },
  checklist: {
    displayKey: 'h5p.libraries.H5P.Checklist',
    iconType: 'Checklist',
    h5pTitle: 'Checklist',
  },
  flashcards: {
    displayKey: 'h5p.libraries.H5P.Flashcards',
    iconType: 'Flashcards',
    h5pTitle: 'Study Cards',
  },
  image_hotspots: {
    displayKey: 'h5p.libraries.H5P.ImageHotspots',
    iconType: 'Image hotspots',
    h5pTitle: 'Hotspots',
  },
  image_slider: {
    displayKey: 'h5p.libraries.H5P.ImageSlider',
    iconType: 'Image slider',
    h5pTitle: 'Image Slider',
  },
  find_the_hotspots: {
    displayKey: 'h5p.libraries.H5P.ImageHotspotQuestion',
    iconType: 'Image hotspot question',
    h5pTitle: 'Hotspot search',
  },
  drag_the_words: {
    displayKey: 'h5p.libraries.H5P.DragText',
    iconType: 'Drag text',
    h5pTitle: 'Drag the Words',
  },
  task: {
    displayKey: 'h5p.libraries.H5P.Task',
    iconType: 'Task',
    h5pTitle: 'Task',
  },
  quiz: {
    displayKey: 'h5p.libraries.H5P.QuestionSet',
    iconType: 'Question set',
    h5pTitle: 'Quiz',
  },
  audio: {
    displayKey: 'h5p.libraries.H5P.Audio',
    iconType: 'Audio',
    h5pTitle: 'Audio',
  },
  file_upload: {
    displayKey: 'h5p.libraries.H5P.File',
    iconType: 'File',
    h5pTitle: 'Slides',
  },
  feedback_module: {
    displayKey: 'h5p.libraries.H5P.Feedback',
    iconType: 'Feedback',
    h5pTitle: 'Feedback',
  },
  link: {
    displayKey: 'h5p.libraries.H5P.Link',
    iconType: 'Link',
    h5pTitle: 'Link',
  },
  memory: {
    displayKey: 'h5p.libraries.H5P.MemoryGame',
    iconType: 'Memory game',
    h5pTitle: 'Memory Game',
  },
  sort_the_paragraphs: {
    displayKey: 'h5p.libraries.H5P.SortParagraphs',
    iconType: 'Sort the Paragraphs',
    h5pTitle: 'Sort the Paragraphs',
  },
};

function isSuggestedModuleType(key: string): key is SuggestedModuleType {
  return key in MODULE_TYPE_MAP;
}

export function getModuleTypeConfig(
  key: string | undefined,
): ModuleTypeConfig | undefined {
  return key && isSuggestedModuleType(key) ? MODULE_TYPE_MAP[key] : undefined;
}

interface CourseModuleOutline {
  name: string;
  moduleType: string;
  rawModuleType?: string;
  estimatedTimeMinutes: number;
  whatToCover: string;
  iconPath?: string;
}

export interface CourseSectionOutline {
  headline: string;
  modules: CourseModuleOutline[];
}

export interface CourseOutline {
  title: string;
  totalTimeMinutes: number;
  learningObjectives: string[];
  sections: CourseSectionOutline[];
}

// Skeleton API types
export interface SkeletonRequestPayload {
  topic: string;
  language: LANGUAGES;
  intent: CourseIntent;
  audience: TargetAudience[];
}

export interface SkeletonSectionResponse {
  section_id: number;
  section_title: string;
  section_number: number;
  paragraph_count: number;
}

export interface SkeletonResponse {
  course_title: string;
  estimated_duration_minutes: number;
  learning_objectives: string[];
  sections: SkeletonSectionResponse[];
  intent: CourseIntent;
  language: LANGUAGES;
  audience: TargetAudience[];
}

// Session API types
export interface SessionResponse {
  data: { id: string };
}

export interface WrappedResponse<T> {
  data: T;
}

// Section content API types (per-section)
export interface SectionContentParagraph {
  paragraph_id: number;
  paragraph_number: number;
  module_headline: string;
  what_to_cover: string;
  estimated_duration_minutes: number;
  suggested_module: SuggestedModuleType;
}

interface BulkCreateParagraph {
  paragraph_number: number;
  suggested_module: string;
  module_headline: string;
  what_to_cover: string;
  estimated_duration_minutes: number;
}

interface BulkCreateSection {
  section_number: number;
  paragraphs: BulkCreateParagraph[];
}

export interface BulkCreatePayload {
  language: string;
  sections: BulkCreateSection[];
}

interface BulkCreateModuleResult {
  content_id: string;
  h5p_library: string;
  content_json: string;
  paragraph_number: number;
  estimated_duration_minutes: number;
}

interface BulkCreateSectionResult {
  title: string;
  modules: BulkCreateModuleResult[];
}

export interface BulkCreateResponse {
  sections: BulkCreateSectionResult[];
}

export function buildBulkCreatePayload(
  outline: CourseOutline,
  language: string,
): BulkCreatePayload {
  return {
    language,
    sections: outline.sections.map((section, sectionIndex) => ({
      section_number: sectionIndex + 1,
      paragraphs: section.modules.map((mod, modIndex) => ({
        paragraph_number: modIndex + 1,
        suggested_module:
          getModuleTypeConfig(mod.rawModuleType)?.h5pTitle ?? mod.moduleType,
        module_headline: mod.name,
        what_to_cover: mod.whatToCover,
        estimated_duration_minutes: mod.estimatedTimeMinutes,
      })),
    })),
  };
}

// Chat API types
export interface OutlineSectionApiResponse {
  section_id: number;
  section_title: string;
  section_number: number;
  paragraphs: SectionContentParagraph[];
}

export interface OutlineApiResponse {
  course_title: string;
  estimated_duration_minutes: number;
  learning_objectives: string[];
  sections: OutlineSectionApiResponse[];
  intent: CourseIntent;
  language: LANGUAGES;
  audience: TargetAudience[];
}

export interface ChatApiResponse {
  message: string;
  summary: string[];
  pending_actions: boolean;
  outline?: OutlineApiResponse;
}

export function mapOutlineApiResponse(
  response: OutlineApiResponse,
): CourseOutline {
  return {
    title: response.course_title,
    totalTimeMinutes: response.estimated_duration_minutes,
    learningObjectives: response.learning_objectives ?? [],
    sections: response.sections.map(s => ({
      headline: s.section_title,
      modules: (s.paragraphs ?? []).map(p => {
        const config = getModuleTypeConfig(p.suggested_module);
        return {
          name: p.module_headline,
          moduleType: config?.displayKey ?? p.suggested_module,
          rawModuleType: p.suggested_module,
          estimatedTimeMinutes: p.estimated_duration_minutes,
          whatToCover: p.what_to_cover,
        };
      }),
    })),
  };
}

// Chat message types
export const MESSAGE_TYPES = {
  USER: 'user',
  AI: 'ai',
  PREVIEW: 'preview',
} as const;

export type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];

export interface ChatMessage {
  type: MessageType;
  content: string;
  summary?: string[];
  pendingActions?: boolean;
}

export const AI_ERROR_CODES = {
  GENERIC: 'generic',
  TIMEOUT: 'timeout',
} as const;

export type AiErrorCode = (typeof AI_ERROR_CODES)[keyof typeof AI_ERROR_CODES];

const AI_ERROR_TRANSLATION_KEYS: Record<AiErrorCode, string> = {
  [AI_ERROR_CODES.GENERIC]: 'guidedCourseCreation.errorDialog.aiGenericError',
  [AI_ERROR_CODES.TIMEOUT]: 'guidedCourseCreation.errorDialog.aiTimeout',
};

export function getAiErrorTranslationKey(code: AiErrorCode): string {
  return AI_ERROR_TRANSLATION_KEYS[code];
}
